import { useState } from 'react';
import { copyToClipboard } from '../lib/clipboard';
import { t, tp } from '../lib/i18n';

interface Props {
  links: Record<string, string>;
  shareTexts?: Record<string, string>;
  referralCode: string;
}

// "base" → translated; the social platforms are brand names so they stay as-is.
function platformLabel(key: string): string {
  if (key === 'base') return t('share.platform.base');
  if (key === 'twitter') return 'X / Twitter';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

const SHARE_URLS: Record<string, (link: string, text: string) => string> = {
  linkedin: (link) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
  twitter: (link, text) =>
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`,
  facebook: (link) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
  reddit: (link, text) =>
    `https://www.reddit.com/submit?url=${encodeURIComponent(link)}&title=${encodeURIComponent(text)}`,
};

export default function SharePanel({ links, shareTexts, referralCode }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  async function copy(key: string, value: string) {
    await copyToClipboard(value);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
  }

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>{t('share.title')}</h3>
        <span className="badge">{tp('share.codeBadge', { code: referralCode })}</span>
      </div>

      <div className="share-grid">
        {Object.entries(links).map(([key, link]) => (
          <div className="card" key={key} style={{ padding: '0.75rem' }}>
            <div className="small muted">{platformLabel(key)}</div>
            <div className="small" style={{ wordBreak: 'break-all', margin: '0.25rem 0' }}>{link}</div>
            <div className="row" style={{ marginTop: '0.5rem' }}>
              <button onClick={() => copy(key, link)}>
                {copied === key ? t('share.copied') : t('share.copyLink')}
              </button>
              {SHARE_URLS[key] && (
                <a
                  className="badge"
                  href={SHARE_URLS[key](link, shareTexts?.[key] ?? link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', padding: '0.4rem 0.7rem' }}
                >
                  {t('share.share')}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {shareTexts && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ margin: '0 0 0.5rem' }}>{t('share.examples')}</h4>
          {Object.entries(shareTexts).map(([key, text]) => (
            <div className="card" key={key} style={{ marginBottom: '0.5rem' }}>
              <div className="flex-between">
                <span className="small muted">{platformLabel(key)}</span>
                <button className="secondary" onClick={() => copy(`text-${key}`, text)}>
                  {copied === `text-${key}` ? t('share.copiedText') : t('share.copyText')}
                </button>
              </div>
              <div className="small" style={{ marginTop: '0.5rem' }}>{text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
